import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import { FaExclamationTriangle } from 'react-icons/fa';
import ConfirmationModal from './ConfirmationModal';
import { useServiceCatalog } from './ServiceCatalogContext';
import { questionnaires } from '../data/questionnaireData';
const PageContainer = styled.div`
  max-width: 800px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #444;
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Textarea = styled.textarea`
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
`;

const Select = styled.select`
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  background: #fff;
`;

const FileFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const UploadedFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #e4f5eb;
  border-radius: 4px;
  padding: 10px 15px;
  font-size: 0.95rem;
  color: #1a7a4c;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #005eb8;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
`;

const UploadProgress = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const CheckboxGroup = styled.div`
    margin-top: 1rem;
    label {
        display: flex;
        align-items: center;
        margin-bottom: 0.75rem;
        cursor: pointer;
    }
    input {
        margin-right: 0.75rem;
    }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
`;

const BackButton = styled.button`
  padding: 15px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  margin-top: 1rem;
  font-weight: bold;
  cursor: pointer;
  &:hover {
    background-color: #5a6268;
  }
`;

const StepButton = styled.button`
  padding: 15px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 1rem;
  width: 100%;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const EmergencyStopMessage = styled.div`
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
    padding: 1.5rem;
    border-radius: 8px;
    margin-top: 2rem;
    text-align: center;

    h3 {
        margin-top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
    }
`;

// --- Questionnaire Data Structure ---
const universalCoreQuestions = {
    title: "Step 1: Core Information",
    fields: [
        { name: 'fullName', label: 'Full Legal Name (must match Passport/ID)', type: 'text', required: true },
        { name: 'dob', label: 'Date of Birth', type: 'date', required: true },
        { name: 'gpPractice', label: 'Registered NHS GP Practice Name & Postcode', type: 'textarea', required: true },
    ],
    consent: {
        name: 'gpConsent',
        label: 'GP Notification Consent',
        options: [
            { value: 'consent', label: 'I consent to a copy of this medical letter being shared with my regular GP if clinically indicated.' },
            { value: 'no_consent', label: 'I do not consent to this letter being shared with my GP. I accept full responsibility for updating my own medical records.' },
        ]
    },
    redFlags: {
        name: 'emergencySymptoms',
        label: 'Are you currently experiencing any of the following symptoms? (Select all that apply)',
        options: [
            'Severe, crushing chest pain, or pain radiating to your jaw/left arm',
            'Sudden, severe shortness of breath or difficulty breathing',
            'Sudden facial drooping, arm weakness, or slurred speech',
            'Uncontrolled bleeding or coughing up blood',
            'Active thoughts of self-harm, suicide, or severe psychiatric distress',
            'None of the above',
        ]
    }
};

// --- Field Rendering Helper ---
const renderField = (field, formState, handleChange) => {
    switch (field.type) {
        case 'text':
        case 'date':
            return <Input id={field.name} name={field.name} type={field.type} value={formState[field.name] || ''} onChange={handleChange} required={field.required} />;
        case 'textarea':
            return <Textarea id={field.name} name={field.name} value={formState[field.name] || ''} onChange={handleChange} required={field.required} />;
        case 'radio':
            return (
                <CheckboxGroup>
                    {field.options.map((opt, index) => (
                        <label key={`${field.name}-${opt}-${index}`}>
                            <input type="radio" name={field.name} value={opt} checked={formState[field.name] === opt} onChange={handleChange} required={field.required} />
                            {opt}
                        </label>
                    ))}
                </CheckboxGroup>
            );
        case 'select':
            return (
                <Select id={field.name} name={field.name} value={formState[field.name] || ''} onChange={handleChange} required={field.required}>
                    <option value="" disabled>Please select...</option>
                    {field.options.map((opt, index) => (
                        <option key={`${field.name}-${opt}-${index}`} value={opt}>{opt}</option>
                    ))}
                </Select>
            );
        default:
            return null;
    }
};

// Some questions ask for a supporting file (e.g. an HIV test result or chest
// X-ray report for certain visa medical certificates) rather than a text
// answer - these upload immediately on selection, separately from the rest
// of the form, so the doctor can see them regardless of whether the customer
// finishes the text questions in the same sitting.
const FileQuestionField = ({ field, uploadedFile, uploadState, onSelect, onRemove }) => {
    if (uploadedFile) {
        return (
            <UploadedFileRow>
                <span>&#10003; {uploadedFile.originalname || 'File uploaded'}</span>
                <RemoveFileButton type="button" onClick={onRemove}>Remove</RemoveFileButton>
            </UploadedFileRow>
        );
    }
    if (uploadState?.uploading) {
        return <UploadProgress>Uploading... {Math.round(uploadState.progress || 0)}%</UploadProgress>;
    }
    return (
        <FileFieldWrapper>
            <Input
                type="file"
                id={field.name}
                required={field.required}
                onChange={(e) => e.target.files[0] && onSelect(e.target.files[0])}
            />
        </FileFieldWrapper>
    );
};

const Questionnaire = () => {
    const { productId } = useParams();
    const { user, saveQuestionnaireAnswers, fetchUserProfile, uploadQuestionnaireFile, removeQuestionnaireFile } = useAuth();
    const [fileUploads, setFileUploads] = useState({});
    const { allServices } = useServiceCatalog();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [formState, setFormState] = useState({
        fullName: '',
        dob: '',
        gpPractice: '',
        gpConsent: '',
        emergencySymptoms: {},
    });

    const [showEmergencyStop, setShowEmergencyStop] = useState(false);

    const order = user?.orders?.find(o =>
        o.items.some(item => item.id === productId) &&
        (o.status === 'awaiting_submission' || o.status === 'rejected')
    );
    const service = allServices.find(s => s.id === productId);
    // Prefer the service's own admin-authored questionnaire; fall back to the
    // legacy named template it used before, then to a generic default.
    const productQuestions = service?.questionnaire
        || (service && questionnaires[service.requiresQuestionnaire])
        || questionnaires['default'];

    useEffect(() => {
        // Initialize form state with keys for the dynamic questions
        const initialProductState = productQuestions.reduce((acc, q) => ({ ...acc, [q.name]: '' }), {});
        const existingAnswers = order?.questionnaireAnswers || {};
        const emergencySymptoms = existingAnswers.emergencySymptoms || {};

        setFormState(prev => ({
            ...prev,
            ...initialProductState,
            ...existingAnswers,
            emergencySymptoms, // Ensure emergencySymptoms is an object
        }));
    }, [productQuestions, order]);

    useEffect(() => {
        // Immediately show emergency stop if any red flag is checked (except 'None')
        const emergencyKeys = Object.keys(formState.emergencySymptoms).filter(key => key !== 'None of the above');
        const isEmergency = emergencyKeys.some(key => formState.emergencySymptoms[key]);
        setShowEmergencyStop(isEmergency);
    }, [formState.emergencySymptoms]);


    useEffect(() => {
        if (user) {
            setFormState(prev => ({
                ...prev,
                fullName: user.name || '',
                dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : '',
            }));
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e) => {
        const { name, value, checked } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                [value]: checked,
            }
        }));
    };

    const handleEmergencyCancel = () => {
        setShowConfirmation(true);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleNextStep = () => {
        if (showEmergencyStop) {
            handleEmergencyCancel();
            return;
        }
        setCurrentStep(2);
    };

    const handleFileQuestionChange = async (questionName, file) => {
        if (!order) return;
        setFileUploads(prev => ({ ...prev, [questionName]: { uploading: true, progress: 0 } }));
        const result = await uploadQuestionnaireFile(order.orderId, questionName, file, (progress) => {
            setFileUploads(prev => ({ ...prev, [questionName]: { uploading: true, progress } }));
        });
        setFileUploads(prev => ({ ...prev, [questionName]: { uploading: false, progress: 100 } }));
        if (result.success) {
            toast.success('File uploaded.');
            await fetchUserProfile(user.token);
        } else {
            toast.error(result.error || 'Upload failed.');
        }
    };

    const handleFileQuestionRemove = async (questionName) => {
        if (!order) return;
        const result = await removeQuestionnaireFile(order.orderId, questionName);
        if (result.success) {
            toast.success('File removed.');
            await fetchUserProfile(user.token);
        } else {
            toast.error(result.error || 'Failed to remove file.');
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!order) {
            toast.error("Could not find a relevant order to update.");
            return;
        }

        const missingRequiredFile = productQuestions.find(
            (q) => q.type === 'file' && q.required && !order.questionnaireFiles?.[q.name]?.filename
        );
        if (missingRequiredFile) {
            toast.error(`Please upload: ${missingRequiredFile.label}`);
            return;
        }

        const result = await saveQuestionnaireAnswers(order.orderId, formState); // Corrected this line

        if (result.success) {
            toast.success("Questionnaire answers saved successfully!");
            await fetchUserProfile(user.token); // Refresh user data in context
        } else {
            toast.error(result.error || "Failed to save answers.");
        }
        navigate('/account');
    };
    const confirmCancellation = async () => {
        if (!order) {
            toast.error("Could not find the relevant order to cancel.");
            setShowConfirmation(false);
            return;
        }

        // setIsCancelling(true); // This state is no longer used, but we can keep the logic structure
        setShowConfirmation(false);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${order.orderId}/customer-cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                },
                body: JSON.stringify({ isEmergency: true }) // Send the emergency flag
            });

            const result = await response.json();

            if (result.success) {
                toast.success("Your order has been cancelled. Please seek immediate medical attention.");
                // Redirect to a page with emergency resources
                navigate('/emergency-info');
            } else {
                toast.error(result.error || "Failed to cancel order.");
            }
        } catch (err) {
            toast.error("An error occurred. Please contact support.");
        }
    };

    return (
        <PageContainer>
            <Title>Medical Questionnaire for {service?.name || 'Your Service'}</Title>
            {currentStep === 1 && (
                <Form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }}>
                    <h3>{universalCoreQuestions.title}</h3>
                    {universalCoreQuestions.fields.map(field => (
                        <FormGroup key={field.name}>
                            <Label htmlFor={field.name}>{field.label}</Label>
                            {renderField(field, formState, handleInputChange)}
                        </FormGroup>
                    ))}
                    <FormGroup>
                        <Label>{universalCoreQuestions.consent.label}</Label>
                        <CheckboxGroup>
                            {universalCoreQuestions.consent.options.map(opt => (
                                <label key={opt.value}>
                                    <input type="radio" name="gpConsent" value={opt.value} checked={formState.gpConsent === opt.value} onChange={handleInputChange} required />
                                    {opt.label}
                                </label>
                            ))}
                        </CheckboxGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{universalCoreQuestions.redFlags.label}</Label>
                        <CheckboxGroup>
                            {universalCoreQuestions.redFlags.options.map(opt => (
                                <label key={opt}>
                                    <input type="checkbox" name="emergencySymptoms" value={opt} checked={!!formState.emergencySymptoms[opt]} onChange={handleCheckboxChange} />
                                    {opt}
                                </label>
                            ))}
                        </CheckboxGroup>
                    </FormGroup>

                    {showEmergencyStop && (
                        <EmergencyStopMessage>
                            <h3><FaExclamationTriangle /> Medical Emergency Warning</h3>
                            <p>You have selected a symptom that may indicate a medical emergency. Please proceed to cancel your order and seek immediate medical attention.</p>
                        </EmergencyStopMessage>
                    )}

                    <StepButton type="submit">
                        {showEmergencyStop ? 'Proceed to Cancel' : 'Next Step'}
                    </StepButton>
                </Form>
            )}

            {currentStep === 2 && (
                <Form onSubmit={handleFormSubmit}>
                    <h3>Step 2: Service Specific Questions</h3>
                    {productQuestions.map((field) => (
                        <FormGroup key={field.name}>
                            <Label htmlFor={field.name}>{field.label}</Label>
                            {field.type === 'file' ? (
                                <FileQuestionField
                                    field={field}
                                    uploadedFile={order?.questionnaireFiles?.[field.name]}
                                    uploadState={fileUploads[field.name]}
                                    onSelect={(file) => handleFileQuestionChange(field.name, file)}
                                    onRemove={() => handleFileQuestionRemove(field.name)}
                                />
                            ) : renderField(field, formState, handleInputChange)}
                        </FormGroup>
                    ))}
                    <StepButton type="submit">Submit Questionnaire</StepButton>
                </Form>
            )}

            <ButtonContainer>
                <BackButton onClick={handleBack}>Back to Account</BackButton>
            </ButtonContainer>

            <ConfirmationModal
                isOpen={showConfirmation}
                title="Confirm Medical Emergency"
                message="Are you sure you want to cancel your order? This action suggests you may have a medical emergency. Please contact 999 or go to your nearest A&E immediately."
                onConfirm={confirmCancellation}
                onCancel={() => setShowConfirmation(false)}
                confirmText="Yes, Cancel Order"
                cancelText="Go back, I made a mistake"
            />
        </PageContainer>
    );
};

export default Questionnaire;